/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
import "./FormRender.scss";
import { createBeforeFormLoadEvent } from "./Events/CreateBeforeFormLoadEvent";
import { createFormRenderEvent } from "./Events/CreateFormRenderEvent";
import { getExternalConfiguration } from "./GetExternalConfiguration";
import { CaptchaRender } from "./Captcha/CaptchaRender";
import { ValidationPatterns } from "../../../Common/Constants/Validation";
import { EventFormRender } from "./EventManagement/EventFormRender";
var FormRender = /** @class */ (function () {
    function FormRender() {
    }
    FormRender.renderForm = function (form, formHtmlContent, renderingStatus) {
        var _this = this;
        form.FormPlaceholders.forEach(function (placeholder) {
            placeholder.Element.dispatchEvent(createFormRenderEvent());
            var formFragment = _this.createFormDocumentFragment(formHtmlContent);
            _this.processForm(formFragment);
            _this.addControlsPrefix(formFragment, placeholder.Element.getAttribute(_this.dataFormBlockPrefixAttribute));
            placeholder.Element.appendChild(formFragment);
            _this.reinjectScripts(placeholder.Element);
            CaptchaRender.renderCaptcha(placeholder.Element);
            EventFormRender.render(form, placeholder.Element, renderingStatus);
        });
    };
    FormRender.loadForm = function (form) {
        var _this = this;
        form.FormPlaceholders.forEach(function (placeholder) {
            var _a;
            placeholder.Element.dispatchEvent(createBeforeFormLoadEvent());
            if (((_a = getExternalConfiguration()) === null || _a === void 0 ? void 0 : _a.hideProgressBar) !== true) {
                var formLoaderDiv = document.createElement("div");
                formLoaderDiv.className = _this.formLoaderClassName;
                placeholder.Element.appendChild(formLoaderDiv);
            }
        });
    };
    FormRender.removeLoader = function (form) {
        var _this = this;
        form.FormPlaceholders.forEach(function (placeholder) {
            var formLoaders = placeholder.Element.getElementsByClassName(_this.formLoaderClassName);
            for (var i = formLoaders.length - 1; i >= 0; --i) {
                formLoaders[i].parentNode.removeChild(formLoaders[i]);
            }
        });
    };
    FormRender.reinjectScripts = function (root, createScriptFunction) {
        if (!createScriptFunction) {
            createScriptFunction = function (ownerDocument) { return ownerDocument.createElement("script"); };
        }
        var scripts = root.querySelectorAll("script");
        scripts.forEach(function (element) {
            var script = createScriptFunction(element.ownerDocument);
            [].forEach.call(element.attributes, function (attr) {
                script.setAttribute(attr.nodeName, attr.nodeValue);
            });
            [].forEach.call(element.childNodes, function (n) { return script.appendChild(n); });
            element.parentNode.insertBefore(script, element);
            element.parentNode.removeChild(element);
        });
    };
    FormRender.processForm = function (node) {
        // Bug 3486532
        var fixPattern = function (selector, brokenPattern, fixedPattern) {
            var brokenInputs = node.querySelectorAll(selector);
            Array.from(brokenInputs !== null && brokenInputs !== void 0 ? brokenInputs : [])
                .filter(function (input) { return input.pattern === brokenPattern.source; })
                .forEach(function (input) {
                // fix pattern attribute
                input.setAttribute("pattern", fixedPattern.source);
            });
        };
        fixPattern("form input[type='tel']", ValidationPatterns.phoneNumber, ValidationPatterns.phoneNumberV2);
        fixPattern("form input[type='email']", ValidationPatterns.email, ValidationPatterns.emailV2);
        fixPattern("form input[type='url']", ValidationPatterns.url, ValidationPatterns.urlV2);
    };
    FormRender.addControlsPrefix = function (node, controlIdPrefix) {
        if (!controlIdPrefix || controlIdPrefix.trim().length === 0) {
            return;
        }
        var labels = node.querySelectorAll("label[for]");
        var newIds = {};
        labels.forEach(function (label) {
            var controlId = label.getAttribute("for");
            var newId = newIds[controlId];
            if (newId) {
                label.setAttribute("for", newId);
            }
            else {
                var control = node.querySelector("[id='".concat(controlId, "']"));
                if (control && control.tagName !== "DATALIST") {
                    newId = controlIdPrefix + controlId;
                    newIds[controlId] = newId;
                    control.id = newId;
                    label.setAttribute("for", newId);
                    var list = control.getAttribute("list");
                    if (list && list.length) {
                        var listElement = node.querySelector("datalist[id=".concat(list, "]"));
                        if (listElement) {
                            var newListId = controlIdPrefix + listElement.id;
                            listElement.id = newListId;
                            control.setAttribute("list", newListId);
                        }
                    }
                }
            }
        });
    };
    FormRender.createFormDocumentFragment = function (formHtmlContent) {
        var result = document.createDocumentFragment();
        var hostElement = document.createElement("div");
        if (formHtmlContent) {
            hostElement.insertAdjacentHTML("beforeend", formHtmlContent); // CodeQL [SM0012] Safe use per threat model: The form editor allows admins to inject the Form HTML
        }
        while (hostElement.hasChildNodes()) {
            result.appendChild(hostElement.firstChild);
        }
        return result;
    };
    FormRender.formLoaderClassName = "formLoader";
    FormRender.dataFormBlockPrefixAttribute = "data-formControlsPrefix";
    return FormRender;
}());
export { FormRender };
